import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import StandardLayout from "components/StandardLayout";
import StockIssuesLeftMenu from "./leftMenu/StockIssuesLeftMenu.jsx";
import { useStockIssues } from "./useStockIssues";
import StockIssuesGroupedList from "./StockIssuesGroupedList";
import ScanFreshness from "./ScanFreshness";
import { useStatusUndo } from "./useStatusUndo.jsx";
import DismissedDrawer from "./DismissedDrawer";
import { useProjectSearch } from "database/useProjectSearch";
import { getProductsDetailById } from "services/supabaseSearch";
import { projectHasDrift } from "./contactDrift";
import {
  COLORS,
  FONTSIZE,
  FONTWEIGHT,
} from "helpers/designConstants";

export default function StockIssuesPage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [severity, setSeverity] = useState("all");
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [selectedRep, setSelectedRep] = useState(null);
  const [facetSearch, setFacetSearch] = useState(null);
  const [projectSearchTerm, setProjectSearchTerm] = useState("");

  const filters = useMemo(
    () => ({
      // `updated` is a client-side filter (drift is computed from
      // snapshot vs live). Don't pass it to the server — it isn't a
      // severity and Supabase doesn't know about drift. Fetched set
      // stays the full active queue; visibleProjects narrows below.
      severity:
        severity === "all" || severity === "updated" ? undefined : severity,
    }),
    [severity]
  );

  const { searchResults } = useProjectSearch();
  const projectLookup = useMemo(
    () =>
      Object.fromEntries(
        (searchResults?.projects || []).map((p) => [p.id, p])
      ),
    [searchResults]
  );

  const {
    projects,
    lastScanAt,
    loading,
    error,
    reload,
    reconcileUpdated,
    updateStatus,
    batchUpdate,
  } = useStockIssues(filters, projectLookup);

  const [productsById, setProductsById] = useState({});
  useEffect(() => {
    const needed = new Set();
    for (const p of projects) for (const i of p.issues) needed.add(i.product_id);
    const missing = Array.from(needed).filter((id) => !(id in productsById));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      let resolved = {};
      try {
        resolved = await getProductsDetailById(missing);
      } catch (e) {
        console.error("Product detail fetch failed", e);
        toast.error("Failed to load product detail");
      }
      if (cancelled) return;
      setProductsById((prev) => {
        const next = { ...prev };
        // Mark every requested id with either the resolved product or null.
        // The null sentinel prevents the effect from re-fetching forever when
        // a product_id has no matching row (orphaned FK, deleted product).
        for (const id of missing) next[id] = resolved[id] ?? null;
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [projects, productsById]);

  // Sidebar facets always see the full severity-filtered set so counts don't
  // shrink when the user narrows by distributor/rep. Card list uses the fully
  // filtered set below. Free-text term matches project name OR any issue's
  // product item number (e.g. "GXJ-2") so a rep can find a project by either.
  const visibleProjects = useMemo(() => {
    const term = projectSearchTerm.trim().toLowerCase();
    return projects.filter((p) => {
      if (selectedDistributor && p.distributor !== selectedDistributor) return false;
      if (selectedRep && p.rep !== selectedRep) return false;
      // "Updated" filter — show only projects with at least one drifted
      // row. Severity filter is server-side; this is purely client-side.
      if (severity === "updated" && !projectHasDrift(p)) return false;
      if (term) {
        const nameMatch = p.name.toLowerCase().includes(term);
        const itemMatch = p.issues.some((i) => {
          const prod = productsById[i.product_id];
          return prod?.itemNumber?.toLowerCase().includes(term);
        });
        if (!nameMatch && !itemMatch) return false;
      }
      return true;
    });
  }, [projects, selectedDistributor, selectedRep, severity, projectSearchTerm, productsById]);

  const [dismissedDrawerOpen, setDismissedDrawerOpen] = useState(false);

  // Both hooks flip the passed IDs back to `new`. Separate bundles so a
  // dismiss + a contacted action don't merge into one confusing toast.
  const { record: recordDismiss } = useStatusUndo({
    verb: "dismissed",
    onUndo: async (ids) => {
      await batchUpdate(ids, "new");
    },
  });
  const { record: recordContacted } = useStatusUndo({
    verb: "contacted",
    countBy: "call",
    unitLabel: "project",
    onUndo: async (ids) => {
      await batchUpdate(ids, "new");
    },
  });

  const safeUpdateStatus = async (id, status, notes) => {
    try {
      const result = await updateStatus(id, status, notes);
      // Only record if the server confirmed the transition.
      if (status === "dismissed" && result?.status === "dismissed") {
        recordDismiss([id]);
      }
      return result;
    } catch (e) {
      toast.error(`Update failed: ${e.message || e}`);
      throw e;
    }
  };

  const safeBatchUpdate = async (ids, status, notes) => {
    try {
      const result = await batchUpdate(ids, status, notes);
      if (result.skipped?.length > 0) {
        toast.info(
          `${result.updated.length} updated, ${result.skipped.length} skipped`
        );
      }
      const updatedIds = result.updated?.map((r) => r.id) ?? [];
      if (updatedIds.length > 0) {
        if (status === "dismissed") recordDismiss(updatedIds);
        else if (status === "contacted") recordContacted(updatedIds);
      }
      return result;
    } catch (e) {
      toast.error(`Batch update failed: ${e.message || e}`);
      throw e;
    }
  };

  const body = (() => {
    // Only show the loading message on the very first load (no data
    // yet). Auto-refreshes while data is already rendered stay silent
    // so the scrollable list doesn't unmount + re-mount at scroll 0.
    if (loading && projects.length === 0) {
      return <StatusMessage>Loading stock issues…</StatusMessage>;
    }
    if (error)
      return (
        <StatusMessage error>
          Error: {String(error.message || error)}
        </StatusMessage>
      );
    if (projects.length === 0)
      return <StatusMessage muted>No active stock issues.</StatusMessage>;
    if (visibleProjects.length === 0)
      return (
        <StatusMessage muted>
          No projects match the current filters.
        </StatusMessage>
      );
    return (
      <StockIssuesGroupedList
        projects={visibleProjects}
        productsById={productsById}
        onUpdateStatus={safeUpdateStatus}
        onBatchUpdate={safeBatchUpdate}
      />
    );
  })();

  return (
    <StandardLayout
      isMenuOpen={isMenuOpen}
      setIsMenuOpen={setIsMenuOpen}
      leftMenuContent={
        <StockIssuesLeftMenu
          projects={projects}
          severity={severity}
          onSeverityChange={setSeverity}
          selectedDistributor={selectedDistributor}
          selectedRep={selectedRep}
          onDistributorChange={setSelectedDistributor}
          onRepChange={setSelectedRep}
          facetSearch={facetSearch}
          onFacetSearchChange={setFacetSearch}
          onMenuClose={() => setIsMenuOpen(false)}
          onOpenDismissed={() => setDismissedDrawerOpen(true)}
        />
      }
    >
      <div className="grid h-full grid-rows-[auto_1fr]">
        <HeaderStrip>
          <PageTitle>Stock Issues</PageTitle>
          <SearchInput
            type="text"
            placeholder="Search projects or item #…"
            value={projectSearchTerm}
            onChange={(e) => setProjectSearchTerm(e.target.value)}
          />
          <RefreshWrapper>
            <ScanFreshness lastScanAt={lastScanAt} />
          </RefreshWrapper>
        </HeaderStrip>

        <div className="overflow-y-auto bg-secondary p-3">{body}</div>
      </div>
      <DismissedDrawer
        show={dismissedDrawerOpen}
        onClose={() => setDismissedDrawerOpen(false)}
        projectLookup={projectLookup}
        onChange={(updatedRow) => {
          // Splice the undismissed row into place — no full reload, so the
          // rep doesn't lose their scroll position. Fall back to reload if
          // the drawer didn't hand up a row (shouldn't happen in practice).
          if (updatedRow) reconcileUpdated(updatedRow);
          else reload();
        }}
      />
    </StandardLayout>
  );
}

const HeaderStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  padding: 10px 12px;
  background: ${COLORS.tertiary};
  border-bottom: 1px solid ${COLORS.stormtechOrange};
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: ${FONTSIZE.medium};
  font-weight: ${FONTWEIGHT.heavy};
  color: ${COLORS.text};
  text-transform: uppercase;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  max-width: 480px;
  border: 1px solid ${COLORS.secondary};
  border-radius: 6px;
  padding: 6px 10px;
  font-size: ${FONTSIZE.small};
  background: ${COLORS.primaryInverted};
  color: ${COLORS.text};
  transition: 0.2s;

  &:hover {
    border-color: ${COLORS.stormtechOrange};
  }

  &:focus {
    outline: 1px solid ${COLORS.stormtechOrange};
    border-color: ${COLORS.stormtechOrange};
  }
`;

const RefreshWrapper = styled.div`
  margin-left: auto;
`;

const StatusMessage = styled.div`
  padding: 24px;
  text-align: center;
  font-size: ${FONTSIZE.small};
  color: ${({ error, muted }) =>
    error ? COLORS.alertRed : muted ? COLORS.stormtechGrey : COLORS.text};
`;
